import { Component, Input, OnInit } from '@angular/core';
import { EasFieldType, EasFormGroup, EasFormService } from '@fry/components/common/easform';
import { EasBuilderField, EasBuilderGroup } from '@fry/components/common/form-builder/builder.interfaces';
import { BuilderFormService } from '@fry/components/common/form-builder/form.service';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, filter, forkJoin, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { FilterTableService } from './filters-table.service';
import { NotifyService } from '@fry/lib/utils';
import { SearchFilter } from '@fry/lib/list';


interface FilterForm  {
  form: EasFormGroup,
  unsubscriber: Subject<void>
};

@Component({
    selector: 'eas-filters-selector',
    templateUrl: './filters-selector.component.html',
    styleUrls: ['./filters-selector.component.css', '../table-controls.common.css'],
    providers: [],
    standalone: false
})
export class FiltersSelectorComponent implements OnInit {
  @Input() itemLabels: Record<'single' | 'plural', string> = { single: $localize `item`, plural: $localize `items` };
  private destroyed$ = new Subject<void>();

  private _filterValue = new BehaviorSubject<SearchFilter | null>(null);
  private filterValue = this._filterValue.asObservable().pipe(
    tap(val => {
      console.log("SF: Before filter", val);
    }),
    filter(itm => itm !== null),
    map(value => {
      console.log("SF: After filter", value);
      return value as SearchFilter;
    })
  ) as Observable<SearchFilter>;

  filterForms: FilterForm[] = [];

  visibleFilters$ = new BehaviorSubject<EasBuilderField[]>([]);
  availableFilters: EasBuilderField[] = this.filterService.availableFilters;

  easForm: EasFormGroup | null = null;


  constructor(
    private easform: EasFormService,
    private formService: BuilderFormService,
    private filterService: FilterTableService,
    private notify: NotifyService,
  ) {
    this.filterValue.subscribe(value => {
      console.log(value);
      console.log('Searching');
      this.filterService.applyNormalFilters(value);
    });
  }

  initialiseForm() {
    this.createForm().subscribe(form => {
      this.filterForms.push(form);
    });
  }

  createForm(filterDef?: EasBuilderField, value?: any) {
    const filterDefs: EasBuilderField[] = [
      {
        id: "column",
        type: EasFieldType.Field,
        fieldType: "simpleSelect",

        options: {
          updateOnOpen: true,
          categoriesFactory: (_easControl, value) => {
            const usedFilters = this.filterForms.map(form => {
              return form.form.getControl('column')?.control.value;
            })
            const options = this.filterService.availableFilters.map(itm => {
              let enabled;
              if (itm.id === value) {
                enabled = true;
              } else {
                enabled = !usedFilters.includes(itm.id);
              }
              return {id: itm.id, name: itm.options.label, disabled: !enabled}
            });
            return options;
          },
          key: 'id',
          placeholder: $localize `Select the filter`
        }
      }
    ]

    let innerValue = {};
    if (filterDef !== undefined) {
      filterDefs.push(filterDef);
      innerValue = {
        column: filterDef?.id,
        [filterDef.id]: value
      };
    }

    const buildForm: EasBuilderGroup = {
      id: 'root',
      fields: filterDefs,
      type: EasFieldType.Group,
      fieldType: 'why?'
    }

    return this.formService.buildForm(buildForm, { user: '__bulk__' }).pipe(
      map(group => {
        const form = this.easform.createForm(group, innerValue)
        const unsub = new Subject<void>();

        form.getControl('column')?.control.valueChanges.pipe(
          takeUntil(unsub),
          distinctUntilChanged(),
          switchMap(filterId => {
            if (!filterId) {
              return of(undefined)
            }
            const filterDef = this.availableFilters.find(itm => itm.id === filterId);
            if (filterDef === undefined) {
              throw new Error("Unknown filter");
            }
            return this.formService.buildField(filterDef);
          }),
          map(element => {
            if (form.controls.length > 1) {
              form.removeControl(form.controls[1].id);
            }
            if (element !== undefined) {
              form.addControl(element);
            }
            if (this.filterForms[this.filterForms.length - 1].form.controls.length > 1) {
              this.initialiseForm();
            }
          })

        ).subscribe(_ => {
        })

        form.getDebouncedChanges({ debounceTime: 1000 }).pipe(
          takeUntil(unsub),
        ).subscribe(_ => {
          this.updateFilterValue();
        })

        return {
          form: form,
          unsubscriber: unsub
        };
      })
    )
  }

  private updateFilterValue() {
    const value = this.filterForms.reduce((prev, curr) => {
      const value = curr.form.getValue();
      const key = value.column;
      if (key && value[key] !== null && value[key] !== undefined && value[key] !== "") {
        prev[key] = value[key];
      }
      return prev;
    }, {} as SearchFilter)

    this._filterValue.next(value);
  }

  ngOnInit(): void {
    this.setupFilters().subscribe(res => {
      this.filterForms = res;
      this.initialiseForm();
    });
  }

  setupFilters() {
    const filter = this.filterService.initialFilter || {};
    const setups: Observable<FilterForm>[] = [];
    Object.entries(filter).forEach(([key, value]) => {
      const fltr = this.availableFilters.find(itm => itm.id === key);
      if (fltr !== undefined)
        setups.push(this.createForm(fltr, value));
    })
    if (setups.length === 0) {
      return of([]);
    }

    return forkJoin(setups);
  }

  removeFilter(easForm: FilterForm) {
    easForm.unsubscriber.next();
    easForm.unsubscriber.complete();
    const idx = this.filterForms.indexOf(easForm);
    this.filterForms.splice(idx, 1);
    this.updateFilterValue();
    if (this.filterForms.length === 0) {
      this.initialiseForm();
    }
  }

  clearFilters() {
    this.filterForms.forEach(form => {
      form.unsubscriber.next();
      form.unsubscriber.complete();
    })
    this.filterForms = [];

    this.updateFilterValue();
    this.filterService.clearFilters();
    this.initialiseForm();
  }

  apply() {
    if (this.easForm?.control !== null && this.easForm?.control.invalid) {
      this.notify.warning('Ensure there are values with each filter');
      return
    }
    this.filterValue.pipe(
      take(1),
    ).subscribe(value => {
      this.filterService.applyNormalFilters(value);
    })
  }

  isFilterVisible(columnId: string): boolean {
    return this.filterService.isFilterVisible(columnId);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
